import { useRef, useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "../ui/command";
import { Camera, Loader2, Upload, X, Wand2, CheckCircle2, AlertCircle, Link as LinkIcon, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { PriceChip } from "../common/PriceChip";
import { showCreditReceipt } from "../../lib/creditReceipt";

// Resize / compress an image file to keep the payload small for vision APIs.
async function resizeImage(file, maxSide = 1280, quality = 0.82) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, w, h);
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve({ blob, dataUrl: reader.result });
      reader.readAsDataURL(blob);
    }, "image/jpeg", quality);
  });
}

/**
 * Dialog that captures / uploads a shelf photo and sends it to the
 * vision backend. Parent receives `onApply(items)` with the detected
 * ingredient-level counts (qty already in the ingredient's base unit).
 */
export function ShelfPhotoDialog({ open, onOpenChange, restaurantId, zone, onApply }) {
  const [preview, setPreview] = useState(null);
  const [b64, setB64] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null); // {provider, items: [...]}
  const [error, setError] = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open || !restaurantId) return;
    api.getIngredients(restaurantId).then(setIngredients).catch(() => {});
  }, [open, restaurantId]);

  const reset = () => {
    setPreview(null); setB64(null); setResult(null); setError(null); setAnalyzing(false);
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Formato no soportado — usa JPG, PNG o WEBP");
      return;
    }
    reset();
    try {
      const { dataUrl } = await resizeImage(file);
      setPreview(dataUrl);
      setB64(String(dataUrl).split(",")[1]);
    } catch (e) {
      toast.error("No se pudo leer la imagen");
    }
  };

  const handleAnalyze = async () => {
    if (!b64) return;
    setAnalyzing(true); setError(null);
    try {
      const r = await api.analyzeShelfPhoto(restaurantId, b64, zone);
      setResult(r);
      showCreditReceipt(r._credits_receipt);
      if (!r.items || r.items.length === 0) {
        toast.warning("La IA no detectó productos reconocibles");
      } else {
        const matched = r.items.filter((i) => i.ingredient_id).length;
        toast.success(`${r.items.length} producto(s) detectado(s)`, {
          description: `${matched} coinciden con tu inventario · provider: ${r.provider}`,
        });
      }
    } catch (e) {
      setError(e.message || "Error al analizar");
      toast.error(e.message || "Error al analizar la foto");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleApply = () => {
    if (!result?.items) return;
    const toApply = result.items.filter((i) => i.ingredient_id);
    if (toApply.length === 0) {
      toast.error("Ningún producto detectado coincide con tu inventario");
      return;
    }
    onApply(toApply);
    onOpenChange(false);
    reset();
  };

  // Link a detected item manually to an ingredient. Persists the alias so
  // future shelf analyses match directly.
  const handleLink = async (idx, ingredient) => {
    const item = result.items[idx];
    try {
      await api.saveVisionAlias(restaurantId, item.detected_name, ingredient.ingredient_id);
      setResult((prev) => ({
        ...prev,
        items: prev.items.map((it, i) => i === idx ? {
          ...it,
          ingredient_id: ingredient.ingredient_id,
          matched_name: ingredient.name,
          matched_via: "alias",
          unit: ingredient.unit,
        } : it),
      }));
      toast.success("Vinculado. La IA lo recordará la próxima vez.");
    } catch (e) {
      toast.error(e.message || "No se pudo vincular");
    }
  };

  const handleUnlink = async (idx) => {
    const item = result.items[idx];
    try {
      await api.saveVisionAlias(restaurantId, item.detected_name, null);
      setResult((prev) => ({
        ...prev,
        items: prev.items.map((it, i) => i === idx ? {
          ...it, ingredient_id: null, matched_name: null, matched_via: null, unit: null,
        } : it),
      }));
      toast.info("Vínculo eliminado");
    } catch (e) {
      toast.error(e.message || "Error");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="shelf-photo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-primary" /> Foto de estantería con IA
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Saca una foto clara de la estantería o barra. La IA contará las botellas visibles
            y rellenará automáticamente el conteo para los productos que reconozca.
          </p>

          {!preview ? (
            <div className="border-2 border-dashed rounded-lg p-8 text-center space-y-3">
              <Camera className="w-10 h-10 text-muted-foreground mx-auto" />
              <p className="text-sm text-muted-foreground">Sube o captura una imagen</p>
              <div className="flex justify-center gap-2">
                <input
                  ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
                  capture="environment" className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="shelf-file-input"
                />
                <Button onClick={() => fileRef.current?.click()} data-testid="shelf-upload-btn">
                  <Upload className="w-4 h-4 mr-1.5" /> Elegir foto
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="relative rounded-lg overflow-hidden border">
                <img src={preview} alt="Estantería" className="w-full max-h-64 object-contain bg-muted" data-testid="shelf-preview-img" />
                <Button
                  variant="outline" size="icon" className="absolute top-2 right-2 h-7 w-7 bg-background/90"
                  onClick={reset}
                  data-testid="shelf-clear-btn"
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>

              {!result && !analyzing && (
                <Button onClick={handleAnalyze} className="w-full gap-2" data-testid="shelf-analyze-btn">
                  <Wand2 className="w-4 h-4" /> Analizar con IA
                  <PriceChip restaurantId={restaurantId} action="vision_shelf" compact className="ml-1" />
                </Button>
              )}
              {analyzing && (
                <div className="border rounded-lg p-4 flex items-center gap-3 text-sm bg-muted/40">
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  Analizando la estantería… puede tardar unos segundos.
                </div>
              )}
              {error && (
                <div className="border rounded-lg p-3 text-sm bg-destructive/10 text-destructive flex items-start gap-2" data-testid="shelf-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {error}
                </div>
              )}
              {result && (
                <div className="border rounded-lg p-3 space-y-1.5 text-sm" data-testid="shelf-results">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Detectado ({result.items.length})</span>
                    <Badge variant="outline" className="text-[10px]">{result.provider}</Badge>
                  </div>
                  {result.items.length === 0 ? (
                    <p className="text-muted-foreground text-xs">Sin resultados. Prueba con una foto más cercana.</p>
                  ) : (
                    <ul className="space-y-1 max-h-60 overflow-y-auto">
                      {result.items.map((it, i) => (
                        <li
                          key={i}
                          className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded ${it.ingredient_id ? "bg-emerald-50 text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200" : "bg-muted text-muted-foreground"}`}
                          data-testid={`shelf-result-${i}`}
                        >
                          {it.ingredient_id ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0" />
                          ) : (
                            <AlertCircle className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                          )}
                          <span className="flex-1 truncate">
                            <span className="font-medium">{it.detected_name}</span>
                            {it.matched_name && it.matched_name !== it.detected_name && (
                              <span className="ml-1 text-[10px] opacity-70">→ {it.matched_name}</span>
                            )}
                            {it.matched_via === "alias" && (
                              <Badge variant="outline" className="ml-1 text-[9px] gap-1 h-4 px-1 border-emerald-300">
                                <Sparkles className="w-2.5 h-2.5" /> aprendido
                              </Badge>
                            )}
                          </span>
                          <span className="tabular-nums whitespace-nowrap text-[11px]">
                            {it.count}× @ {it.avg_fill_pct}%
                          </span>
                          {it.ingredient_id ? (
                            <Button
                              type="button" variant="ghost" size="icon" className="h-6 w-6"
                              onClick={() => handleUnlink(i)}
                              title="Quitar vínculo"
                              data-testid={`shelf-unlink-${i}`}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          ) : (
                            <IngredientLinkPicker
                              ingredients={ingredients}
                              onPick={(ing) => handleLink(i, ing)}
                              testId={`shelf-link-${i}`}
                            />
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={handleApply}
            disabled={!result || !result.items?.some((i) => i.ingredient_id)}
            data-testid="shelf-apply-btn"
          >
            Aplicar al conteo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ShelfPhotoDialog;

function IngredientLinkPicker({ ingredients, onPick, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button" variant="outline" size="sm" className="h-6 px-2 text-[10px] gap-1"
          data-testid={testId}
        >
          <LinkIcon className="w-2.5 h-2.5" /> Vincular
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="end">
        <Command>
          <CommandInput placeholder="Buscar producto…" className="h-9" />
          <CommandList>
            <CommandEmpty>Sin resultados</CommandEmpty>
            <CommandGroup>
              {ingredients.map((ing) => (
                <CommandItem
                  key={ing.ingredient_id}
                  value={`${ing.name} ${ing.unit}`}
                  onSelect={() => { onPick(ing); setOpen(false); }}
                >
                  <span className="flex-1 truncate text-xs">{ing.name}</span>
                  <span className="text-[10px] text-muted-foreground ml-1">{ing.unit}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
